/**
 * `PostureMonitor` — facade của toàn bộ Posture Engine (Prompt.md §12–§18).
 *
 * Đây là **API duy nhất** mà tầng platform cần biết: `push(vision)` mỗi frame, `tick(now)` khi
 * không có frame. Mọi thứ bên trong (đo → smoothing → so baseline → phân loại → máy trạng thái)
 * là chi tiết cài đặt, và không có dòng nào chạm tới DOM, timer hay giờ hệ thống — nhờ vậy app
 * React Native sau này dùng lại nguyên khối (§5, §29).
 */

import { resolveConfig, type AppConfig } from "@studystraight/shared-config";
import type {
  MonitorSnapshot,
  PostureBaseline,
  PostureEvent,
  PostureIssueType,
  PostureMetrics,
  PostureResult,
  TrackingState,
  VisionResult,
} from "@studystraight/shared-types";

import { RuleBasedPostureClassifier, type PostureClassifier } from "./classifier.js";
import { PostureSmoothing } from "./PostureSmoothing.js";
import { PostureStateMachine } from "./PostureStateMachine.js";
import { createMetricExtractor, type PlacementMetricExtractor } from "./metrics.js";

const ALL_METRIC_KEYS = ["headAngle", "torsoAngle", "shoulderTilt", "distanceRatio"] as const;

const UNMEASURED_METRICS: PostureMetrics = {
  headAngle: { value: Number.NaN, available: false },
  torsoAngle: { value: Number.NaN, available: false },
  shoulderTilt: { value: Number.NaN, available: false },
  distanceRatio: { value: Number.NaN, available: false },
};

const NO_ISSUES: Record<PostureIssueType, "NONE"> = {
  HEAD_DOWN: "NONE",
  LEANING_FORWARD: "NONE",
  TOO_CLOSE: "NONE",
  SHOULDER_TILT: "NONE",
};

/**
 * Kết quả khi chưa có frame nào đo được: điểm 100 nhưng **cả 4 metric bị loại**.
 *
 * Không phải "tư thế hoàn hảo" — là "chưa đo được gì". Hai thứ này phân biệt bằng
 * `excludedMetrics`, và UI phải đọc `tracking` chứ không đọc điểm để biết có đang thấy trẻ hay
 * không. Nếu ở đây trả 0 điểm, phiên học nào cũng mở đầu bằng "CHƯA TỐT" trong lúc trẻ còn chưa
 * kịp ngồi vào bàn.
 */
const UNMEASURED_RESULT: PostureResult = {
  status: "GOOD",
  postureScore: 100,
  issues: { ...NO_ISSUES },
  excludedMetrics: [...ALL_METRIC_KEYS],
  metrics: UNMEASURED_METRICS,
  confidence: 0,
};

export interface PostureMonitorOptions {
  baseline: PostureBaseline;
  /** Mặc định `resolveConfig(baseline.placement)`. Truyền vào để tuning ở Settings > Developer. */
  config?: AppConfig;
  /** §30 — đường sang classifier ML mà không sửa gì ở đây. */
  classifier?: PostureClassifier;
}

export class PostureMonitor {
  private readonly config: AppConfig;
  private readonly extractor: PlacementMetricExtractor;
  private readonly classifier: PostureClassifier;
  private readonly smoothing: PostureSmoothing;
  private readonly stateMachine: PostureStateMachine;

  private lastResult: PostureResult = UNMEASURED_RESULT;
  private lastTracking: TrackingState = "LOST";

  constructor(private readonly options: PostureMonitorOptions) {
    this.config = options.config ?? resolveConfig(options.baseline.placement);
    if (this.config.placement !== options.baseline.placement) {
      throw new Error(
        `Config placement ${this.config.placement} không khớp baseline ${options.baseline.placement}`,
      );
    }
    this.extractor = createMetricExtractor(this.config.placement);
    this.classifier =
      options.classifier ??
      new RuleBasedPostureClassifier(this.config.posture, this.config.weights);
    this.smoothing = new PostureSmoothing({
      posture: this.config.posture,
      temporal: this.config.temporal,
    });
    this.stateMachine = new PostureStateMachine(this.config.temporal);
  }

  get baseline(): PostureBaseline {
    return this.options.baseline;
  }

  get placement(): AppConfig["placement"] {
    return this.config.placement;
  }

  push(vision: VisionResult): MonitorSnapshot {
    const frame = this.smoothing.pushFrame(vision);
    this.lastTracking = frame.tracking;

    if (frame.vision === null) {
      // Frame bị bỏ: giữ nguyên kết quả cũ, KHÔNG phát event. Mất tracking thì dừng mọi đồng hồ.
      if (frame.tracking === "LOST") this.stateMachine.interrupt();
      return this.snapshot(vision.timestamp, []);
    }

    const raw = this.extractor.extract(frame.vision, this.options.baseline, this.config.posture);
    const metrics = this.smoothing.smoothMetrics(raw, frame.vision.timestamp);
    const judgement = this.classifier.classify(metrics);

    this.lastResult = {
      status: judgement.status,
      postureScore: judgement.postureScore,
      issues: judgement.issues,
      excludedMetrics: judgement.excludedMetrics,
      metrics,
      confidence: frame.vision.confidence,
    };

    const transition = this.stateMachine.push(this.lastResult, frame.vision.timestamp);
    return {
      result: this.lastResult,
      state: transition.state,
      tracking: frame.tracking,
      events: transition.events,
      timestamp: frame.vision.timestamp,
    };
  }

  /**
   * Nhịp "không có frame nào".
   *
   * Cần thiết chứ không phải tiện dụng: khi trẻ rời khỏi bàn, pose engine không phát hiện người
   * nên provider trả `null` và `push()` **không hề được gọi** — không có nhịp này thì hệ thống
   * đứng mãi ở trạng thái cuối cùng và tin rằng trẻ vẫn đang ngồi đó.
   */
  tick(now: number): MonitorSnapshot {
    this.lastTracking = this.smoothing.trackingAt(now);
    if (this.lastTracking === "LOST") this.stateMachine.interrupt();
    return this.snapshot(now, []);
  }

  reset(): void {
    this.smoothing.reset();
    this.stateMachine.reset();
    this.lastResult = UNMEASURED_RESULT;
    this.lastTracking = "LOST";
  }

  private snapshot(timestamp: number, events: readonly PostureEvent[]): MonitorSnapshot {
    return {
      result: this.lastResult,
      state: this.stateMachine.current,
      tracking: this.lastTracking,
      events,
      timestamp,
    };
  }
}
